import { Box, ButtonBase, Card, Stack, Typography as MuiTypography, useTheme } from "@mui/material";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import { FoodsIcon, ToggleSwitch, Typography, type IconMappingProps } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import type { KeyboardEvent, ReactNode } from "react";

type TransportResourceKind = "leg" | "circuit";

type TransportResourceModeIcon = {
    key: string;
    icon: IconMappingProps | "flightTakeoff";
    label: string;
};

type TransportResourceCardProps = {
    appearance?: "legacy" | "product-v2";
    kind: TransportResourceKind;
    title: string;
    subtitle?: string;
    route?: {
        origin: string;
        destination: string;
    };
    modeIcons?: TransportResourceModeIcon[];
    meta?: string[];
    selected?: boolean;
    selectable?: boolean;
    disabled?: boolean;
    onSelect?: () => void;
    actions?: ReactNode;
};

type TransportMetaTagProps = {
    appearance: "legacy" | "product-v2";
    label: string;
    backgroundColor: string;
    color: string;
    icon?: "leg" | "route";
};

const EMPTY_MODE_ICONS: TransportResourceModeIcon[] = [];
const EMPTY_META: string[] = [];
const TransportToggleIcon = ({ selected }: { selected: boolean }) => (
    <Box
        component="span"
        className="MuiSwitch-thumb"
        sx={{
            display: "grid",
            placeItems: "center",
            width: (theme) => theme.spacing(2),
            height: (theme) => theme.spacing(2),
            borderRadius: (theme) => theme.spacing(1),
        }}
    >
        <FoodsIcon icon={selected ? "viewPwd" : "hidePwd"} size={1.75} />
    </Box>
);

const TransportMetaTag = ({ appearance, label, backgroundColor, color, icon }: TransportMetaTagProps) => (
    <Box
        component="span"
        sx={{
            height: (theme) => theme.spacing(2.75),
            minWidth: 0,
            display: "inline-flex",
            alignItems: "center",
            gap: icon ? 0.25 : 0,
            px:
                appearance === "legacy" ?
                    icon ? 0.25
                    :   0.5
                :   1,
            py: 0.25,
            borderRadius: (theme) => theme.spacing(0.5),
            backgroundColor,
            color,
            whiteSpace: "nowrap",
            "& svg": {
                display: "block",
                color,
            },
            "& svg path": {
                fill: "currentColor",
            },
        }}
    >
        {icon && <FoodsIcon icon={icon} size={2} />}
        {appearance === "legacy" ?
            <MuiTypography
                component="span"
                sx={(theme) => ({
                    color,
                    fontSize: theme.spacing(1.5),
                    fontWeight: 600,
                    lineHeight: "160%",
                })}
            >
                {label}
            </MuiTypography>
        :   <Typography component="span" variant="small-semibold" sx={{ color }}>
                {label}
            </Typography>
        }
    </Box>
);

const TransportModeIconBadge = ({ icon, label }: Omit<TransportResourceModeIcon, "key">) => {
    const theme = useTheme();

    return (
        <Box
            component="span"
            title={label}
            aria-label={label}
            sx={{
                width: theme.spacing(2.75),
                height: theme.spacing(2.75),
                borderRadius: theme.spacing(0.5),
                display: "grid",
                placeItems: "center",
                color: theme.custom.grey[100],
                background: theme.custom.grey.gradientDark,
                flexShrink: 0,
                "& svg": {
                    display: "block",
                    color: theme.custom.grey[100],
                },
                "& svg path": {
                    fill: "currentColor",
                },
            }}
        >
            {icon === "flightTakeoff" ?
                <FlightTakeoffIcon sx={{ fontSize: theme.spacing(2.5) }} />
            :   <FoodsIcon icon={icon} size={2} />}
        </Box>
    );
};

const TransportRouteSummary = ({ origin, destination }: NonNullable<TransportResourceCardProps["route"]>) => {
    const theme = useTheme();

    return (
        <Stack
            aria-label={`${origin} ${destination}`}
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ color: theme.custom.grey[2000], minWidth: 0 }}
        >
            <Typography variant="small-regular" noWrap>
                {origin}
            </Typography>
            <Box aria-hidden="true" component="span" sx={{ display: "inline-flex", flexShrink: 0 }}>
                <FoodsIcon icon="arrowRightShort" size={1.5} />
            </Box>
            <Typography variant="small-regular" noWrap>
                {destination}
            </Typography>
        </Stack>
    );
};

export const TransportResourceCard = ({
    appearance = "legacy",
    kind,
    title,
    subtitle,
    route,
    modeIcons = EMPTY_MODE_ICONS,
    meta = EMPTY_META,
    selected = false,
    selectable = true,
    disabled = false,
    onSelect,
    actions,
}: TransportResourceCardProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const kindIcon = kind === "leg" ? "leg" : "route";
    const kindLabel =
        kind === "leg" ? t("transport.resource.kind.leg", "Leg") : t("transport.resource.kind.itinerary", "Itinerary");
    const circuitColor = theme.custom.dataviz.colorsWithExtra[12];
    const legColor = theme.custom.dataviz.colorsWithExtra[13];
    const refrigeratedColor = theme.custom.dataviz.colorsWithExtra[2];
    const cardBorderStroke = theme.custom.strokes.containerDivider;
    const cardBorderColor = selected ? theme.custom.grey[2800] : theme.custom.grey[500];
    const cardBackgroundColor = selected ? theme.custom.grey[300] : theme.custom.grey[200];
    const handleSelectKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget) return;
        if (disabled || !selectable || !onSelect) return;
        if (event.key !== "Enter" && event.key !== " ") return;

        event.preventDefault();
        onSelect();
    };

    const cardContent = (
        <Stack direction="row" alignItems="stretch" gap={2} sx={{ minWidth: 0, width: "100%" }}>
            <Stack gap={1} sx={{ minWidth: 0, flex: 1 }}>
                <Stack gap={0.5} sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
                            {modeIcons.map((modeIcon) => (
                                <TransportModeIconBadge
                                    key={modeIcon.key}
                                    icon={modeIcon.icon}
                                    label={modeIcon.label}
                                />
                            ))}
                        </Stack>

                        {appearance === "legacy" ?
                            <MuiTypography
                                variant="body1"
                                sx={{
                                    color: theme.custom.grey[2800],
                                    fontSize: theme.spacing(2),
                                    fontWeight: 600,
                                    lineHeight: "160%",
                                    minWidth: 0,
                                }}
                                noWrap
                            >
                                {title}
                            </MuiTypography>
                        :   <Typography
                                variant="body-semibold"
                                sx={{ minWidth: 0, color: theme.custom.grey[2800] }}
                                noWrap
                            >
                                {title}
                            </Typography>
                        }
                    </Stack>

                    {route && appearance === "product-v2" ?
                        <TransportRouteSummary {...route} />
                    : route || subtitle ?
                        appearance === "legacy" ?
                            <MuiTypography
                                variant="body2"
                                sx={{
                                    color: theme.custom.grey[2000],
                                    fontSize: theme.spacing(1.5),
                                    lineHeight: "160%",
                                }}
                                noWrap
                            >
                                {route ? `${route.origin} -> ${route.destination}` : subtitle}
                            </MuiTypography>
                        :   <Typography variant="small-regular" sx={{ color: theme.custom.grey[2000] }} noWrap>
                                {route ? `${route.origin} -> ${route.destination}` : subtitle}
                            </Typography>

                    :   null}
                </Stack>

                {meta.length > 0 && (
                    <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
                        <TransportMetaTag
                            appearance={appearance}
                            label={kindLabel}
                            icon={kindIcon}
                            backgroundColor={kind === "circuit" ? circuitColor.variant[100] : legColor.variant[100]}
                            color={kind === "circuit" ? circuitColor.main : legColor.main}
                        />
                        {meta.map((item) => {
                            const isRefrigerated = item.toLowerCase().includes("refrigerated");

                            return (
                                <TransportMetaTag
                                    appearance={appearance}
                                    key={item}
                                    label={item}
                                    backgroundColor={
                                        isRefrigerated ? refrigeratedColor.variant[100] : theme.custom.grey[400]
                                    }
                                    color={isRefrigerated ? refrigeratedColor.variant[700] : theme.custom.grey[2500]}
                                />
                            );
                        })}
                    </Stack>
                )}
            </Stack>

            {(actions || selectable) && (
                <Stack
                    alignItems="flex-end"
                    justifyContent="space-between"
                    gap={1.5}
                    sx={{ alignSelf: "stretch", flexShrink: 0 }}
                    onClick={(event) => event.stopPropagation()}
                >
                    {actions && (
                        <Stack direction="row" alignItems="center" gap={0.25}>
                            {actions}
                        </Stack>
                    )}

                    {selectable && appearance === "legacy" && (
                        <ButtonBase
                            disabled={disabled}
                            aria-label={
                                selected ?
                                    t("transport.map.remove_from_map", "Remove from map")
                                :   t("transport.map.show_on_map", "Show on map")
                            }
                            onClick={(event) => {
                                event.stopPropagation();
                                onSelect?.();
                            }}
                            sx={{
                                width: theme.spacing(5.5),
                                height: theme.spacing(3),
                                p: 0.5,
                                borderRadius: theme.spacing(3),
                                flexShrink: 0,
                                justifyContent: selected ? "flex-end" : "flex-start",
                                backgroundColor: selected ? theme.palette.primary[500] : theme.custom.grey[400],
                                transition: theme.transitions.create("background-color", {
                                    duration: theme.transitions.duration.shortest,
                                }),
                                "&.Mui-disabled": {
                                    opacity: 0.5,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    width: theme.spacing(2),
                                    height: theme.spacing(2),
                                    borderRadius: theme.spacing(1),
                                    display: "grid",
                                    placeItems: "center",
                                    color: selected ? theme.palette.primary[500] : theme.custom.grey[2000],
                                    background: theme.custom.grey.gradientLight,
                                    boxShadow: theme.custom.shadows.grey,
                                    "& svg": {
                                        color: "currentColor",
                                    },
                                    "& svg path": {
                                        fill: "currentColor",
                                    },
                                }}
                            >
                                <FoodsIcon icon={selected ? "viewPwd" : "hidePwd"} size={1.5} />
                            </Box>
                        </ButtonBase>
                    )}

                    {selectable && appearance === "product-v2" && (
                        <ToggleSwitch
                            checked={selected}
                            disabled={disabled}
                            toggleSize="medium"
                            icon={<TransportToggleIcon selected={false} />}
                            checkedIcon={<TransportToggleIcon selected />}
                            inputProps={{
                                "aria-label":
                                    selected ?
                                        t("transport.map.remove_from_map", "Remove from map")
                                    :   t("transport.map.show_on_map", "Show on map"),
                            }}
                            FormControlLabelProps={{ sx: { m: 0 } }}
                            onChange={(event) => {
                                event.stopPropagation();
                                onSelect?.();
                            }}
                            sx={{
                                m: 0,
                                "& .MuiSwitch-track": {
                                    backgroundColor: theme.custom.toggleSwitch.status.uncheckedColor,
                                },
                                "& .MuiSwitch-thumb": {
                                    display: "grid",
                                    placeItems: "center",
                                    color: theme.custom.toggleSwitch.status.uncheckedIconColor,
                                    background: theme.custom.toggleSwitch.status.handleBackground,
                                    boxShadow: theme.custom.shadows.grey,
                                    "& svg": {
                                        color: "currentColor",
                                        width: theme.spacing(1.75),
                                        height: theme.spacing(1.75),
                                    },
                                    "& svg path": {
                                        fill: "currentColor",
                                    },
                                },
                                "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
                                    color: theme.palette.primary[600],
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                    backgroundColor: theme.palette.primary[600],
                                    opacity: 1,
                                },
                            }}
                        />
                    )}
                </Stack>
            )}
        </Stack>
    );

    const surfaceSx = {
        borderRadius: theme.spacing(1.5),
        borderColor: cardBorderColor,
        borderStyle: cardBorderStroke.style,
        borderWidth: cardBorderStroke.width,
        backgroundColor: cardBackgroundColor,
        boxShadow: theme.custom.shadows.grey,
        flexShrink: 0,
        transition: theme.transitions.create(["border-color", "box-shadow", "background-color"], {
            duration: theme.transitions.duration.shortest,
        }),
        overflow: "hidden",
    };
    const interactiveContent =
        selectable ?
            <Box
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
                onClick={disabled ? undefined : onSelect}
                onKeyDown={handleSelectKeyDown}
                sx={{
                    p: 2,
                    cursor: disabled ? "default" : "pointer",
                    alignItems: "stretch",
                    overflow: "visible",
                    "&:focus-visible": {
                        outline: `${theme.spacing(0.25)} solid ${theme.custom.grey[2800]}`,
                        outlineOffset: theme.spacing(-0.25),
                    },
                }}
            >
                {cardContent}
            </Box>
        :   <Box sx={{ p: 2 }}>{cardContent}</Box>;

    if (appearance === "legacy") {
        return (
            <Card component="article" data-appearance="legacy" variant="outlined" sx={surfaceSx}>
                {interactiveContent}
            </Card>
        );
    }

    return (
        <Box
            component="article"
            data-appearance="product-v2"
            sx={{
                ...surfaceSx,
            }}
        >
            {interactiveContent}
        </Box>
    );
};

export default TransportResourceCard;
